import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import { Avatar } from './Avatar.js';
import { IconChevronDown } from '../icons/index.js';

export interface UserChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Nom complet de l'utilisateur, affiché à côté de l'avatar. */
  name: string;
  /** Initiale(s) de l'avatar. Déduites de `name` si absent (1re lettre). */
  initials?: string;
  /** Photo de l'utilisateur. Prime sur les initiales, qui restent le repli. */
  avatarUrl?: string;
}

/** Première lettre du nom, en capitale, comme repli pour l'avatar. */
function deriveInitials(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

/**
 * UserChip : pilule cliquable d'identite utilisateur (avatar + nom + chevron),
 * rendue par le shell (panneau home).
 *
 * Brand book Taki : h-40px · padding 0 14px 0 7px · gap 10px ·
 * border #EAE9E5 · radius 13px · bg surface. Survol : border-strong + surface-1.
 * Avatar 26px (photo si `avatarUrl`, sinon ink-500 + initiale blanche), nom
 * Inter 500 / 14px / ink-900, chevron 15px en ink-300.
 *
 * @example
 * <UserChip name="Claude Nauer" onClick={openMenu} />
 * <UserChip name="Celia" avatarUrl="/celia.png" onClick={openMenu} />
 */
export const UserChip = forwardRef<HTMLButtonElement, UserChipProps>(function UserChip(
  { className, name, initials, avatarUrl, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex h-10 items-center gap-2.5 rounded-[0.8125rem] border border-border bg-surface pl-[0.4375rem] pr-3.5 transition-colors',
        'hover:border-border-strong hover:bg-surface-1',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        className,
      )}
      {...props}
    >
      <Avatar size={26} src={avatarUrl} initials={initials ?? deriveInitials(name)} />
      <span className="text-sm font-medium text-ink-900">{name}</span>
      <IconChevronDown size={15} className="text-ink-300" />
    </button>
  );
});
